import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { theme, st, SECTIONS, API_BASE_URL, SAVED_CARDIO_KEY, CARDIO_DRAFT_KEY } from './cardio/constants.js';
import PrintSummary from './cardio/components/PrintSummary.js';
import PatientContext from './cardio/sections/PatientContext.js';
import BreathingEfficiency from './cardio/sections/BreathingEfficiency.js';
import AirwayClearance from './cardio/sections/AirwayClearance.js';
import GasExchange from './cardio/sections/GasExchange.js';
import CardiacTolerance from './cardio/sections/CardiacTolerance.js';
import FunctionalCapacity from './cardio/sections/FunctionalCapacity.js';
import RiskComplications from './cardio/sections/RiskComplications.js';
import TreatmentPlan from './cardio/sections/TreatmentPlan.js';
import { getAuthHeaders, userKey } from '../utils/auth.js';
import { pushAssessments } from '../utils/syncAssessments.js';
import { payForDiagnosis } from '../utils/payDiagnosis.js';

export default function CardioPulmonaryForm() {
  const navigate = useNavigate();
  const [section, setSection] = useState('context');
  const [existingId, setExistingId] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);

  const [data, setData] = useState({
    /* S1 – Patient Context */
    name: '', age: '', sex: '', occupation: '', livingSituation: '', hospNo: '', doa: '', ward: '',
    primaryDiagnosis: '', secondaryDiagnosis: '', referralReason: '',
    medications: '', devices: '', ventialtor: '', oxygenDevice: '', drains: '',
    premorbidMobility: '', premorbidADL: '', premorbidExercise: '',
    dischargeDestination: '', dischargeGoals: '', dischargeMobilityTarget: '',
    /* S1 – Red Flags / Safety */
    consciousness: '', temp: '', pr: '', bp: '', rr: '', spo2: '',
    safeForPT: '', redFlagNotes: '',
    redFlags: [],
    /* S2 – Breathing Efficiency */
    breathingPattern: '', observedRR: '', wob: '', tripodPosition: '', pursedLips: '',
    accessoryMuscles: [], breathSounds: [], breathSoundsDetails: '',
    chestExpansion: '', chestSymmetry: '', chestMeasurementUpper: '', chestMeasurementLower: '',
    painLimitingBreathing: '', splinting: '',
    oxygenStatus: 'Room air', oxygenFlow: '', targetSpo2Range: '', ieRatio: '',
    breathingImpairments: [], breathingNotes: '',
    /* S3 – Airway Clearance */
    coughPresent: '', coughStrength: '', coughEffectiveness: '', coughType: '',
    huffing: '', assistedCough: '',
    sputumPresent: '', sputumAmount: '', sputumColor: '', sputumConsistency: '',
    bloodInSputum: '',
    voice: '', swallowing: '', gagReflex: '', aspirationRisk: '',
    secretionManagement: '', suctionRequired: '',
    clearanceImpairments: [], clearanceNotes: '',
    /* S4 – Gas Exchange */
    spo2Rest: '', spo2Sitting: '', spo2Walking: '', spo2Recovery: '',
    desatOnActivity: '', desatAmount: '', desatRecoveryTime: '',
    cyanosis: '', clubbing: '', confusionHypoxia: '',
    capillaryRefill: '', peripheralPulses: '', limbWarmth: '',
    percussionNote: [], fremitus: '',
    abgPH: '', abgPCO2: '', abgPO2: '', abgHCO3: '', abgBE: '', abgFiO2: '', abgInterpretation: '',
    gasExchangeImpairments: [], gasExchangeNotes: '',
    /* S5 – Cardiac Tolerance */
    heartRate: '', bpSystolic: '', bpDiastolic: '',
    heartRhythm: '', heartSounds: '', murmurs: '',
    jvp: '', oedemaPresent: '', oedemaLocation: [], oedemaType: '', oedemaGrade: '',
    anaemia: '',
    chestPainPresent: '', cpProv: '', cpQual: [], cpRad: '', cpSeverity: '', cpTiming: '',
    nyha: '', orthopnea: '', pnd: '',
    hrWithActivity: '', bpWithActivity: '', cardiacRecovery: '', cardiacStopReason: '',
    cardiacImpairments: [], cardiacNotes: '',
    /* S5 – Investigations */
    cxrFindings: '', ctFindings: '', ecgFindings: '',
    echoEDV: '', echoESV: '', echoEF: '', echoFindings: '',
    troponin: '', ckMB: '', bnp: '', lipids: '', bloodWork: '',
    fev1: '', fvc: '', fev1fvc: '', dlco: '', pftPattern: '',
    sputumCulture: '', abgReport: '',
    /* S6 – Functional Capacity */
    currentMobilityLevel: '', walkingDistance: '', walkingAid: '',
    sixMWD: '', sixMWDpredicted: '', sixMWDpercent: '',
    stairsCapacity: '', adlIndependence: '',
    limitingSymptom: '', limitingDomain: '',
    mmrc: '', borgBaseline: '', borgPeak: '',
    preHR: '', preBP: '', preRR: '', preSPO2: '',
    peakHR: '', peakBP: '', peakRR: '', peakSPO2: '', stopReason: '',
    rec1HR: '', rec1SPO2: '', rec3HR: '', rec3SPO2: '', rec5HR: '', rec5SPO2: '',
    hrRecovery1min: '', abnormalHRRecovery: false,
    dischargeReadiness: '', functionalNotes: '',
    /* S7 – Risk */
    atelectasisRisk: '', atelectasisFactors: [],
    pneumoniaRisk: '', pneumoniaFactors: [],
    dvtRisk: '', dvtFactors: [],
    deconditioningRisk: '', deconditioningFactors: [],
    fallRisk: '', fallFactors: [],
    riskNotes: '',
    /* S8 – Plan */
    ptPriorities: [], ptImmediateGoals: '', ptShortTermGoals: '', ptLongTermGoals: '',
    ptTreatmentApproach: [], ptFrequency: '', ptPrecautions: '',
    ptProgressionCriteria: '', planNotes: '',
  });

  const [isProfiling, setIsProfiling]             = useState(false);
  const [impairmentProfile, setImpairmentProfile] = useState(null);
  const [cpDiagTab, setCpDiagTab]                 = useState('ai');
  const [cpAiAutoFilled, setCpAiAutoFilled]       = useState(false);
  const [validationModal, setValidationModal]     = useState(null);
  const [safetyModal, setSafetyModal]             = useState(false);
  const [payGate, setPayGate]                     = useState(null);
  const [payGateError, setPayGateError]           = useState(null);
  const [chatHistory, setChatHistory]             = useState([]);
  const [chatInput, setChatInput]                 = useState('');
  const [chatLoading, setChatLoading]             = useState(false);

  const upd  = (k, v) => setData(p => ({ ...p, [k]: v }));
  const updA = (k, v) => setData(p => ({ ...p, [k]: v }));

  /* ── Gate: redirect to /home if at limit and no prepaid/reopened draft ── */
  useEffect(() => {
    try {
      const draft = JSON.parse(window.localStorage.getItem(userKey(CARDIO_DRAFT_KEY)) || 'null');
      if (draft?._prepaid || draft?.id) return;
    } catch {}
    fetch(`${API_BASE_URL}/api/user/usage`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u && !u.isAdmin && u.tier === 'free' && u.used >= u.limit) navigate('/home'); })
      .catch(() => {});
  }, [navigate]);

  /* ── Restore draft on mount ── */
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(userKey(CARDIO_DRAFT_KEY));
      if (!saved) return;
      const parsed = JSON.parse(saved);
      // Pre-paid assessment: { assessmentId, _prepaid: true }
      if (parsed && parsed._prepaid && parsed.assessmentId) {
        setAssessmentId(parsed.assessmentId);
        window.localStorage.removeItem(userKey(CARDIO_DRAFT_KEY));
        return;
      }
      // Full reopen payload (from assessment history) vs plain auto-save draft
      if (parsed && typeof parsed === 'object' && 'data' in parsed) {
        setData(parsed.data);
        if (parsed.id) setExistingId(parsed.id);
        if (parsed.assessmentId) setAssessmentId(parsed.assessmentId);
        if (parsed.impairmentProfile) {
          setImpairmentProfile(parsed.impairmentProfile);
          setSection(SECTIONS[SECTIONS.length - 1].id);
        }
        if (Array.isArray(parsed.chatHistory) && parsed.chatHistory.length) {
          setChatHistory(parsed.chatHistory);
        }
      } else {
        setData(parsed);
      }
    } catch (e) { /* corrupt draft — ignore */ }
  }, []);

  /* ── Auto-save draft on every data change ── */
  useEffect(() => {
    try {
      window.localStorage.setItem(userKey(CARDIO_DRAFT_KEY), JSON.stringify({
        data,
        id: existingId,
        assessmentId,
        impairmentProfile,
        chatHistory,
      }));
    } catch (e) {
      if (e.name === 'QuotaExceededError') alert('Storage is full. Please save and clear old assessments to continue.');
    }
  }, [data, existingId, assessmentId, impairmentProfile, chatHistory]);

  /* ── Auto: red flags from vitals (S1) and gas exchange (S4) ── */
  useEffect(() => {
    const flags = [];
    const spo2Primary = parseFloat(data.spo2Rest || data.spo2);
    const spo2Walk    = parseFloat(data.spo2Walking);
    const rrv  = parseFloat(data.rr);
    const hrv  = parseFloat(data.pr);
    const displaySpo2 = data.spo2Rest || data.spo2;
    if (spo2Primary > 0 && spo2Primary < 90)  flags.push({ type: 'critical', msg: `SpO₂ ${displaySpo2}% — Critical hypoxaemia. O₂ titration before any PT.` });
    if (spo2Primary >= 90 && spo2Primary < 94) flags.push({ type: 'warning',  msg: `SpO₂ ${displaySpo2}% — Monitor closely during activity.` });
    if (spo2Walk > 0 && spo2Walk < 90)         flags.push({ type: 'critical', msg: `SpO₂ ${data.spo2Walking}% on walking — Critical exertional desaturation.` });
    if (spo2Walk >= 90 && spo2Walk < 94)       flags.push({ type: 'warning',  msg: `SpO₂ ${data.spo2Walking}% on walking — Monitor exertional tolerance closely.` });
    if (rrv > 30) flags.push({ type: 'critical', msg: `RR ${rrv}/min — Severe tachypnoea. Assess WOB before mobilising.` });
    if (rrv < 8 && rrv > 0) flags.push({ type: 'critical', msg: `RR ${rrv}/min — Bradypnoea. Check central drive.` });
    if (hrv > 130) flags.push({ type: 'critical', msg: `HR ${hrv} bpm — Tachycardia. Medical review before PT.` });
    if (hrv < 40 && hrv > 0) flags.push({ type: 'critical', msg: `HR ${hrv} bpm — Severe bradycardia. Do NOT mobilise.` });
    setData(p => ({ ...p, redFlags: flags }));
  }, [data.spo2, data.spo2Rest, data.spo2Walking, data.rr, data.pr]);

  /* ── Auto: EF ── */
  useEffect(() => {
    const edv = parseFloat(data.echoEDV), esv = parseFloat(data.echoESV);
    if (edv > 0 && esv > 0 && edv > esv) upd('echoEF', ((edv - esv) / edv * 100).toFixed(1));
  }, [data.echoEDV, data.echoESV]);

  /* ── Auto: HR recovery ── */
  useEffect(() => {
    const peak = parseFloat(data.peakHR), rec1 = parseFloat(data.rec1HR);
    if (peak > 0 && rec1 > 0) {
      const drop = peak - rec1;
      upd('hrRecovery1min', drop.toFixed(0));
      upd('abnormalHRRecovery', drop < 12);
    }
  }, [data.peakHR, data.rec1HR]);

  /* ── Auto: desaturation ── */
  useEffect(() => {
    const base = parseFloat(data.spo2Rest || data.spo2), nadir = parseFloat(data.spo2Walking);
    if (base > 0 && nadir > 0 && base > nadir) upd('desatAmount', (base - nadir).toFixed(1));
  }, [data.spo2Rest, data.spo2Walking, data.spo2]);

  const sectionRef = useRef(section);
  useEffect(() => { sectionRef.current = section; }, [section]);
  useEffect(() => {
    window.history.pushState(null, '');
    const onPopState = () => {
      const curIdx = SECTIONS.findIndex(s => s.id === sectionRef.current);
      if (curIdx > 0) {
        window.history.pushState(null, '');
        setSection(SECTIONS[curIdx - 1].id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/home');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigate]);

  const performSave = () => {
    setSafetyModal(false);
    const savedId = existingId || crypto.randomUUID();
    const record = {
      id: savedId,
      date: new Date().toISOString(),
      patientName: data.name || 'Unnamed Patient',
      zone: data.primaryDiagnosis || 'CardioPulmonary',
      diagnosis: data.primaryDiagnosis || 'No diagnosis recorded',
      isSubmitted: !!impairmentProfile,
      data,
      impairmentProfile: impairmentProfile || null,
      chatHistory,
    };
    try {
      const existing = JSON.parse(window.localStorage.getItem(userKey(SAVED_CARDIO_KEY)) || '[]');
      const existingIndex = existing.findIndex(r => r.id === savedId);
      const isNewSave = existingIndex < 0;
      const merged = isNewSave
        ? [record, ...existing]
        : existing.map((r, i) => i === existingIndex ? record : r);
      window.localStorage.setItem(userKey(SAVED_CARDIO_KEY), JSON.stringify(merged));
      window.localStorage.removeItem(userKey(CARDIO_DRAFT_KEY));
      pushAssessments('saved-cardio-assessments', merged);
    } catch (e) {
      if (e.name === 'QuotaExceededError') { alert('Storage is full. Please clear old assessments and try again.'); return; }
      console.error('Failed to save cardio assessment:', e);
    }
    setAssessmentId(null);
    navigate('/home');
  };

  const handleSaveAndReturnHome = () => {
    if (data.safeForPT === 'No') { setSafetyModal(true); return; }
    performSave();
  };

  const checkCardioRequirements = () => {
    const missing = [];
    if (!data.primaryDiagnosis?.trim()) missing.push("Primary diagnosis is required");
    const vitalsPresent = [
      data.heartRate || data.pr,
      data.bpSystolic || data.bp,
      data.spo2Rest || data.spo2,
      data.observedRR || data.rr,
    ].filter(v => v?.toString().trim()).length;
    if (vitalsPresent < 2) missing.push("At least 2 vital signs required (HR, BP, SpO2, or RR)");
    if (!data.currentMobilityLevel?.trim() && !data.limitingDomain?.trim())
      missing.push("Functional capacity must be documented (current mobility level or limiting domain)");
    const hasBreathSounds = Array.isArray(data.breathSounds) ? data.breathSounds.length > 0 : !!data.breathSounds?.trim?.();
    const hasInvestigation = data.cxrFindings?.trim() || data.ecgFindings?.trim() || data.pftPattern?.trim() || hasBreathSounds;
    const hasSymptom = data.breathingPattern?.trim() || data.wob?.trim();
    if (!hasInvestigation && !hasSymptom)
      missing.push("At least one clinical finding required: breath sounds, breathing pattern, CXR, ECG, or PFT results");
    return missing;
  };

  const handleProfileImpairments = async () => {
    const missing = checkCardioRequirements();
    if (missing.length > 0) { setValidationModal(missing); return; }
    setIsProfiling(true);
    try {
      const daysPostOnset = data.doa
        ? Math.floor((new Date() - new Date(data.doa)) / (1000 * 60 * 60 * 24))
        : null;
      const payload = {
        medicalContext: { diagnosis: data.primaryDiagnosis, comorbidities: data.secondaryDiagnosis, oxygenDevice: data.oxygenDevice, oxygenFlow: data.oxygenFlow, medications: data.medications, daysPostOnset, safeForPT: data.safeForPT },
        breathingEfficiency: { respiratoryRate: data.observedRR || data.rr, breathingPattern: data.breathingPattern, workOfBreathing: data.wob, accessoryMuscles: data.accessoryMuscles, chestExpansion: data.chestExpansion, chestSymmetry: data.chestSymmetry, ieRatio: data.ieRatio, mmrc: data.mmrc, tripodPosition: data.tripodPosition, pursedLips: data.pursedLips, painLimitingBreathing: data.painLimitingBreathing, breathingImpairments: data.breathingImpairments },
        airwayClearance: { coughStrength: data.coughStrength, coughEffectiveness: data.coughEffectiveness, coughType: data.coughType, huffing: data.huffing, assistedCough: data.assistedCough, sputumPresent: data.sputumPresent, sputumAmount: data.sputumAmount, sputumColor: data.sputumColor, sputumConsistency: data.sputumConsistency, bloodInSputum: data.bloodInSputum, secretionManagement: data.secretionManagement, suctionRequired: data.suctionRequired, aspirationRisk: data.aspirationRisk, clearanceImpairments: data.clearanceImpairments },
        oxygenation: { spo2Rest: data.spo2Rest || data.spo2, spo2Sitting: data.spo2Sitting, spo2Walking: data.spo2Walking, spo2Recovery: data.spo2Recovery, oxygenStatus: data.oxygenStatus, desatOnActivity: data.desatOnActivity, desatAmount: data.desatAmount, desatRecoveryTime: data.desatRecoveryTime, cyanosis: data.cyanosis, clubbing: data.clubbing, confusionHypoxia: data.confusionHypoxia, capillaryRefill: data.capillaryRefill, abgPH: data.abgPH, abgPCO2: data.abgPCO2, abgPO2: data.abgPO2, abgHCO3: data.abgHCO3, abgInterpretation: data.abgInterpretation, gasExchangeImpairments: data.gasExchangeImpairments },
        cardiovascularTolerance: { heartRate: data.heartRate || data.pr, bpSystolic: data.bpSystolic, bpDiastolic: data.bpDiastolic, bp: data.bp, heartRhythm: data.heartRhythm, peakHR: data.peakHR, peakBP: data.peakBP, hrRecovery1min: data.hrRecovery1min, abnormalHRRecovery: data.abnormalHRRecovery, jvp: data.jvp, oedemaPresent: data.oedemaPresent, oedemaLocation: data.oedemaLocation, oedemaGrade: data.oedemaGrade, nyha: data.nyha, chestPainPresent: data.chestPainPresent, echoEF: data.echoEF, bnp: data.bnp, cardiacImpairments: data.cardiacImpairments },
        functionalCapacity: { currentMobilityLevel: data.currentMobilityLevel, walkingDistance: data.walkingDistance, walkingAid: data.walkingAid, sixMWD: data.sixMWD, sixMWDpercent: data.sixMWDpercent, mmrc: data.mmrc, borgBaseline: data.borgBaseline, borgPeak: data.borgPeak, adlIndependence: data.adlIndependence, stairsCapacity: data.stairsCapacity, limitingSymptom: data.limitingSymptom, limitingDomain: data.limitingDomain, dischargeReadiness: data.dischargeReadiness, premorbidMobility: data.premorbidMobility, premorbidExercise: data.premorbidExercise },
        clinicalFindings: { breathSounds: data.breathSounds, breathSoundsDetails: data.breathSoundsDetails, percussionNote: data.percussionNote, heartSounds: data.heartSounds, murmurs: data.murmurs, cxrFindings: data.cxrFindings, ecgFindings: data.ecgFindings, pftPattern: data.pftPattern, fev1: data.fev1, fev1fvc: data.fev1fvc },
        // S7 (Risk & Complications) and S8 (Treatment Plan) are intentionally excluded —
        // the AI generates its own risk assessment and treatment priorities from objective data only.
        autoFlags: data.redFlags,
        dischargeGoals: data.dischargeGoals,
        dischargeMobilityTarget: data.dischargeMobilityTarget,
        dischargeDestination: data.dischargeDestination,
      };
      const aid = assessmentId || crypto.randomUUID();
      if (!assessmentId) setAssessmentId(aid);
      const response = await fetch(`${API_BASE_URL}/api/profile-impairments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...payload, assessmentId: aid }),
      });
      const profile = await response.json();
      if (response.status === 401) { navigate('/home'); return; }
      if (response.status === 402 && profile.pay_required) {
        setPayGate({ assessmentId: profile.assessmentId || aid });
        return;
      }
      if (profile.status === "insufficient_data") {
        const list = (profile.missing || []).map(m => `\n• ${m}`).join("");
        throw new Error(`Cannot generate profile — incomplete assessment:${list}\n\nPlease complete these sections and retry.`);
      }
      if (profile.status === "contradictory_data") {
        const list = (profile.issues || []).map(i => `\n• ${i}`).join("");
        throw new Error(`Conflicting cardiopulmonary findings detected — please review before profiling:${list}`);
      }
      if (!response.ok) throw new Error(profile.error || 'Profiling failed');
      if (profile.status === "partial" || !profile.primaryImpairments)
        throw new Error(profile.message || "Impairment profile incomplete. Please retry.");
      setImpairmentProfile(profile);
      setData(prev => {
        const toStr = (v) => Array.isArray(v) ? v.join('\n') : (typeof v === 'string' ? v : '');
        return {
          ...prev,
          ptShortTermGoals: prev.ptShortTermGoals || toStr(profile.interventionPriorities?.map(p => p.goal || p.domain)),
          ptLongTermGoals: prev.ptLongTermGoals || toStr(profile.dischargeGoals),
          ptProgressionCriteria: prev.ptProgressionCriteria || (typeof profile.progressionCriteria === 'string' ? profile.progressionCriteria : toStr(profile.progressionCriteria?.criteria)),
          ptPrecautions: prev.ptPrecautions || toStr(profile.treatmentPrecautions),
        };
      });
      setCpAiAutoFilled(true);
    } catch (error) {
      console.error('Profiling Error:', error);
      setValidationModal([error.message || 'Failed to generate impairment profile. Please check your connection and try again.']);
    } finally {
      setIsProfiling(false);
    }
  };

  const handleChatQuery = async () => {
    const question = chatInput.trim();
    if (!question || chatLoading) return;
    const nextHistory = [...chatHistory, { role: 'user', text: question }];
    setChatHistory(nextHistory);
    setChatInput('');
    setChatLoading(true);
    try {
      const aid = assessmentId || crypto.randomUUID();
      if (!assessmentId) setAssessmentId(aid);
      const response = await fetch(`${API_BASE_URL}/api/chat-cardio`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          assessmentId: aid,
          question,
          assessment: { diagnosis: data.primaryDiagnosis, limitingDomain: data.limitingDomain, mmrc: data.mmrc, spo2: data.spo2Rest || data.spo2, breathingPattern: data.breathingPattern, wob: data.wob, coughEffectiveness: data.coughEffectiveness, currentMobilityLevel: data.currentMobilityLevel },
          impairmentProfile: impairmentProfile || null,
          messages: nextHistory.slice(-5),
        }),
      });
      if (response.status === 401) { navigate('/home'); return; }
      if (!response.ok) throw new Error('Chat failed');
      const result = await response.json();
      setChatHistory([...nextHistory, { role: 'ai', text: result.answer || 'No response generated.' }]);
    } catch (error) {
      console.error('Chat Error:', error);
      setChatHistory([...nextHistory, { role: 'ai', text: "I couldn't generate a response just now. Please check the backend server and try again." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const curIdx = SECTIONS.findIndex(s => s.id === section);


  return (
    <>
    <div style={st.appWrapper}>

      {/* Print + animation styles */}
      <style>{`
        * { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        input, select, textarea, button { font-family: inherit; font-size: inherit; }
        ::placeholder { color: #94a3b8; opacity: 1; }

        /* Active tap feedback — instant visual response on all buttons */
        .cp-content-pad button:active { opacity: 0.72; transform: scale(0.96); transition: transform 0.07s, opacity 0.07s !important; }

        /* Info (!) button hover */
        .cp-info-btn:hover > span { background: #c7d2fe !important; }

        /* Custom select arrow */
        .cp-content-pad select { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 36px !important; }

        /* Profiler button spinner */
        @keyframes cpSpin { to { transform: rotate(360deg); } }
        .cp-spinner { display: inline-block; width: 15px; height: 15px; border: 2px solid rgba(255,255,255,0.35); border-top-color: #fff; border-radius: 50%; animation: cpSpin 0.7s linear infinite; vertical-align: middle; margin-right: 7px; }

        /* Grid collapse — all auto-fit grids stack to single column on phones */
        @media (max-width: 480px) {
          div[style*="repeat(auto-fit"] { grid-template-columns: 1fr !important; }
        }

        /* Input / select min touch height on small phones */
        @media (max-width: 480px) {
          .cp-content-pad input:not(.cp-table-input),
          .cp-content-pad select { min-height: 44px !important; }
          .cp-scroll-hint { display: block !important; }
        }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @page { margin: 0.5cm; }
        @media print {
          body > *:not(#cp-print-portal) { display: none !important; }
          #cp-print-portal { display: block !important; padding: 24px; background: #fff; font-family: 'Inter', sans-serif; font-size: 11px; color: #0f172a; }
          #cp-print-portal > * { page-break-inside: avoid; }
        }
        @media screen { #cp-print-portal { display: none; } }
        @media (max-width: 560px) {
          .cp-step-label { display: none !important; }
          .cp-nav-title { font-size: 0.78rem !important; }
          .cp-name-badge { display: none !important; }
          .cp-content-pad { padding: 0 10px !important; }
          .cp-nav-circle { width: 22px !important; height: 22px !important; font-size: 0.7rem !important; }
          .cp-step-bar { gap: 2px !important; padding-bottom: 8px !important; }
        }
        @media (max-width: 480px) {
          .cp-section-icon { width: 36px !important; height: 36px !important; font-size: 18px !important; border-radius: 10px !important; }
          .cp-section-title { font-size: 1rem !important; }
          div[style*="minmax(120px"] { grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)) !important; }
        }
        @media (max-width: 400px) {
          .cp-nav-circle { width: 20px !important; height: 20px !important; font-size: 0.65rem !important; }
          .cp-step-bar { gap: 1px !important; }
          .cp-section-icon { width: 30px !important; height: 30px !important; font-size: 15px !important; }
        }
        @media (max-width: 375px) {
          .cp-nav-circle { width: 18px !important; height: 18px !important; font-size: 0.6rem !important; }
        }
        .ai-card-pre { padding: 20px 0 28px; text-align: center; }
        .ai-pre-title { margin: 0 0 10px; color: #1e40af; font-size: 1.3rem; font-weight: 800; }
        .ai-pre-desc { color: #1d4ed8; margin: 0 0 20px; font-size: 1rem; font-weight: 500; line-height: 1.6; }
        .ai-analyze-btn { padding: 16px 32px; font-size: 1.1rem; }
        .ai-results-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 25px; border-bottom: 1px solid #e2e8f0; padding-bottom: 15px; }
        .ai-grid-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-bottom: 20px; }
        @media (max-width: 600px) {
          .ai-card-pre { padding: 10px 0 18px; }
          .ai-pre-title { font-size: 1.05rem !important; }
          .ai-pre-desc { font-size: 0.88rem !important; margin-bottom: 14px !important; }
          .ai-analyze-btn { padding: 14px 18px !important; font-size: 0.95rem !important; width: 100% !important; box-sizing: border-box !important; }
          .ai-results-header h3 { font-size: 1.1rem !important; }
          .ai-grid-2 { grid-template-columns: 1fr !important; gap: 10px !important; }
        }
      `}</style>

      {/* ── VALIDATION MODAL ── */}
      {validationModal && (
        <div
          onClick={() => setValidationModal(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '20px', padding: '28px 28px 24px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease-out' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: 40, height: 40, borderRadius: '12px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>⚠️</div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#0f172a' }}>Incomplete Data</h3>
                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>Fill in the required fields before running analysis</p>
              </div>
            </div>
            <div style={{ background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8' }}>Required to proceed</p>
              {validationModal.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < validationModal.length - 1 ? '10px' : 0 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', marginTop: '6px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.88rem', color: '#1e293b', lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setValidationModal(null)}
              style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}
            >
              Go Back &amp; Fill
            </button>
          </div>
        </div>
      )}

      {/* Print-only summary — rendered via portal as direct body child so print CSS can isolate it */}
      {ReactDOM.createPortal(
        <div id="cp-print-portal">
          <div style={{ borderBottom: '2px solid #4f46e5', paddingBottom: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src="/logo192.png" alt="AssessMonk" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>AssessMonk</div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Physiotherapy Assessment</div>
                </div>
              </div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>assessmonk.com</div>
            </div>
          </div>
          <PrintSummary data={data} impairmentProfile={impairmentProfile} />
        </div>,
        document.body
      )}

      {/* Sticky Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 200, backgroundColor: 'rgba(241,245,249,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${theme.border}` }}>
        <div className="cp-content-pad" style={{ maxWidth: '1060px', margin: '0 auto', padding: '0 20px' }}>

          {/* Row 1: back button | step bar | badges */}
          <header style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '56px', padding: '8px 0' }}>
            <div style={{ flexShrink: 0 }}>
              <button
                type="button"
                aria-label="Back"
                disabled={isProfiling}
                onClick={() => { if (!isProfiling) { if (curIdx > 0) { setSection(SECTIONS[curIdx - 1].id); window.scrollTo({ top: 0, behavior: 'smooth' }); } else { navigate(-1); } } }}
                style={{ minWidth: '44px', minHeight: '44px', padding: '6px 10px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: 'transparent', cursor: isProfiling ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '1.1rem', color: theme.textMuted, lineHeight: 1, transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: isProfiling ? 0.4 : 1 }}
              >
                ←
              </button>
            </div>

            {/* Section progress bar — inline beside back button */}
            <div className="cp-step-bar" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
              {SECTIONS.map((s, i) => {
                const isActive = section === s.id;
                const isDone   = curIdx > i;
                return (
                  <React.Fragment key={s.id}>
                    {i > 0 && (
                      <div style={{ flex: 1, height: '2px', borderRadius: '99px', background: isDone ? theme.success : isActive ? s.color : theme.border, transition: 'background 0.3s' }} />
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: isProfiling ? 'not-allowed' : 'pointer', opacity: isProfiling ? 0.5 : 1 }} onClick={() => { if (!isProfiling) { setSection(s.id); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}>
                      <div className="cp-nav-circle" style={{ width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isDone ? '0.72rem' : '0.88rem', fontWeight: '800', transition: 'all 0.25s', background: isDone ? theme.success : isActive ? s.color : '#e2e8f0', color: isDone || isActive ? '#fff' : '#94a3b8', boxShadow: isActive ? `0 0 0 4px ${s.color}22` : 'none' }}>
                        {isDone ? '✓' : s.icon}
                      </div>
                      <span className="cp-step-label" style={{ fontSize: '0.6rem', fontWeight: isActive ? '700' : '500', color: isActive ? s.color : isDone ? theme.success : '#94a3b8', whiteSpace: 'nowrap' }}>
                        {s.short}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '7px', alignItems: 'center', flexShrink: 0 }}>
              {data.name && (
                <span className="cp-name-badge" style={{ background: '#fef2f2', color: '#ef4444', padding: '3px 10px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '700' }}>{data.name}</span>
              )}
              {data.redFlags.length > 0 && (
                <span style={{ background: '#fef2f2', color: theme.danger, padding: '3px 10px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '800', border: '1px solid #fca5a5' }}>
                  ⚠️ {data.redFlags.length} Red Flag{data.redFlags.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </header>

          {/* Row 2: print button — only on last section */}
          {section === 'plan' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px' }}>
              <button type="button" onClick={() => navigate('/home')}
                style={{ padding: '5px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.surface, cursor: 'pointer', fontWeight: '700', fontSize: '0.78rem', color: '#4f46e5', transition: 'background-color 0.15s, color 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.borderColor = '#a5b4fc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.border; }}>
                ⌂ Homepage
              </button>
              {impairmentProfile && (
                <button type="button" onClick={() => window.print()}
                  style={{ padding: '5px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.surface, cursor: 'pointer', fontWeight: '700', fontSize: '0.78rem', color: '#16a34a', transition: 'background-color 0.15s, color 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.border; }}>
                  <span style={{ fontSize: '1rem' }}>↓</span> PDF
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Content */}
      <div className="cp-content-pad" style={st.container}>

        {/* Red Flag Banner */}
        {data.redFlags.length > 0 && (
          <div role="alert" aria-live="assertive" style={{ backgroundColor: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '16px', padding: '14px 18px', marginBottom: '18px', animation: 'fadeIn 0.3s' }}>
            <div style={{ fontWeight: '800', color: '#dc2626', marginBottom: '8px', fontSize: '0.88rem' }}>⚠️ SAFETY RED FLAGS — Review before PT intervention</div>
            {data.redFlags.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '5px 9px', borderRadius: '7px', marginBottom: '3px', background: f.type === 'critical' ? '#fef2f2' : '#fffbeb', border: `1px solid ${f.type === 'critical' ? '#fca5a5' : '#fcd34d'}`, color: f.type === 'critical' ? '#dc2626' : '#d97706', fontWeight: '600', fontSize: '0.82rem' }}>
                {f.type === 'critical' ? '🔴' : '🟡'} {f.msg}
              </div>
            ))}
          </div>
        )}

        {section === 'context'     && <PatientContext      data={data} upd={upd} />}
        {section === 'breathing'   && <BreathingEfficiency data={data} upd={upd} updA={updA} />}
        {section === 'clearance'   && <AirwayClearance     data={data} upd={upd} updA={updA} />}
        {section === 'gasExchange' && <GasExchange         data={data} upd={upd} updA={updA} />}
        {section === 'cardiac'     && <CardiacTolerance    data={data} upd={upd} updA={updA} />}
        {section === 'function'    && <FunctionalCapacity  data={data} upd={upd} />}
        {section === 'risk'        && <RiskComplications   data={data} upd={upd} updA={updA} />}
        {section === 'plan'        && (
          <TreatmentPlan
            data={data} upd={upd} updA={updA}
            impairmentProfile={impairmentProfile}
            isProfiling={isProfiling}
            handleProfileImpairments={handleProfileImpairments}
            setImpairmentProfile={setImpairmentProfile}
            chatHistory={chatHistory}
            chatInput={chatInput}
            setChatInput={setChatInput}
            chatLoading={chatLoading}
            handleChatQuery={handleChatQuery}
            diagTab={cpDiagTab}
            setDiagTab={setCpDiagTab}
            aiAutoFilled={cpAiAutoFilled}
          />
        )}

        {/* Fixed Bottom Footer */}
        <div style={st.footerBar}>
          <div style={st.footerInner}>
            {curIdx < SECTIONS.length - 1 && (
              <button
                onClick={() => { if (!isProfiling) { setSection(SECTIONS[curIdx + 1].id); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
                style={{ ...st.footerBtn, opacity: isProfiling ? 0.5 : 1 }}
                disabled={isProfiling}
              >
                Continue to {SECTIONS[curIdx + 1].label} →
              </button>
            )}
            {curIdx === SECTIONS.length - 1 && (
              <button onClick={handleSaveAndReturnHome} style={st.footerBtnSuccess}>
                Save & Return Home
              </button>
            )}
          </div>
        </div>

      </div>
    </div>

    {payGate && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: '28px 28px 22px', maxWidth: 380, width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '1.5rem', textAlign: 'center', marginBottom: 10 }}>🔬</div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>Unlock AI Diagnosis</h3>
          <p style={{ margin: '0 0 18px', fontSize: '0.82rem', color: '#64748b', textAlign: 'center', lineHeight: 1.5 }}>
            Your 3 free assessments are used up. Pay <strong>₹19</strong> for this session — unlocks AI diagnosis, refine, chat, and PDF download.
          </p>
          <button
            type="button"
            onClick={() => { setPayGateError(null); payForDiagnosis(payGate.assessmentId, {
              onSuccess: () => { setPayGate(null); setPayGateError(null); handleProfileImpairments(); },
              onCancel:  () => setPayGate(null),
              onError:   (msg) => setPayGateError(msg || 'Payment failed. Please try again.'),
            }); }}
            style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#4f46e5,#3b82f6)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 10 }}
          >
            Pay ₹19 with Razorpay
          </button>
          {payGateError && (
            <div style={{ marginBottom: 10, padding: '9px 13px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: '0.76rem', color: '#991b1b', fontWeight: 600 }}>⚠ {payGateError}</div>
          )}
          <button
            type="button"
            onClick={() => { setPayGate(null); setPayGateError(null); }}
            style={{ width: '100%', padding: '11px', background: 'transparent', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}

    {safetyModal && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 18, padding: '26px 26px 22px', maxWidth: 380, width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '1.6rem', textAlign: 'center', marginBottom: 10 }}>⚠️</div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>Patient Not Safe for PT</h3>
          <p style={{ margin: '0 0 20px', fontSize: '0.82rem', color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>
            This patient is marked <strong style={{ color: '#dc2626' }}>NOT safe for PT</strong>. Do you still want to save and return home?
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => setSafetyModal(false)}
              style={{ flex: 1, padding: '11px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={performSave}
              style={{ flex: 1, padding: '11px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              Save Anyway
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
